import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { useATS } from "../context/ATSContext";
import { AutoTaskAuthContext } from "../../../Context/AutoTaskContext";
import {
  departmentService,
  isEndpointMissing,
  readApiError,
} from "../services/departmentService";
import DepartmentFormDrawer from "../components/departments/DepartmentFormDrawer";
import { useDeptRoutes } from "../components/departments/deptRoutes";
import { scopeLabel } from "../../../utils/departmentTypes";
import {
  DeptTheme,
  EmptyState,
  Hint,
  initialsOf,
  PrimaryButton,
  QuietButton,
  SectionLabel,
  T,
} from "../components/departments/ui";

const HOW_KEY = "hb.departments.howItWorks.dismissed";

const STEPS = [
  {
    n: 1,
    title: "Pick a department",
    body: "Your organization's departments. Separate from the ticket departments unless one is marked as both.",
  },
  {
    n: 2,
    title: "Build its teams and roles",
    body: "Work downwards until you reach the roles people are hired into.",
  },
  {
    n: 3,
    title: "Add a job template to a role",
    body: "The job description and screening questions, written once.",
  },
  {
    n: 4,
    title: "Open a job from that template",
    body: "One click. It arrives knowing its department and its interviewers.",
  },
];

const HowItWorks = ({ onDismiss }) => (
  <Box
    sx={{
      position: "relative",
      bgcolor: T.surface,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius.lg,
      p: { xs: 2, sm: 2.5 },
      mb: 2.5,
    }}
  >
    {onDismiss && (
      <Tooltip title="Hide this">
        <IconButton
          onClick={onDismiss}
          size="small"
          aria-label="Hide the explainer"
          sx={{ position: "absolute", top: 8, right: 8, color: T.faint }}
        >
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    )}

    <SectionLabel sx={{ mb: 1.5 }}>How hiring departments work</SectionLabel>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: { xs: 1.5, lg: 2 },
      }}
    >
      {STEPS.map((step) => (
        <Box key={step.n} sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: T.accentWash,
              color: T.accentDeep,
              display: "grid",
              placeItems: "center",
              fontFamily: T.font,
              fontSize: 12,
              fontWeight: 800,
              flex: "none",
              mt: 0.1,
            }}
          >
            {step.n}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: T.font,
                fontWeight: 700,
                fontSize: 13.5,
                color: T.ink,
                lineHeight: 1.35,
              }}
            >
              {step.title}
            </Typography>
            <Hint sx={{ fontSize: 12.5, mt: 0.15 }}>{step.body}</Hint>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const DepartmentsList = () => {
  const { store_id, user_id } = useATS();
  const navigate = useNavigate();
  // Mounted both inside the ATS module and inside the Daily Tick shell, so where
  // "open this department" goes depends on which one is hosting. See deptRoutes.
  const routes = useDeptRoutes();
  const contextData = useContext(AutoTaskAuthContext) || {};
  const { allUsers } = contextData;

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [apiMissing, setApiMissing] = useState(false);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("active");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHow, setShowHow] = useState(() => {
    try {
      return localStorage.getItem(HOW_KEY) !== "1";
    } catch {
      // Private windows and blocked site data both throw here; the explainer
      // showing again is a much smaller problem than the screen not rendering.
      return true;
    }
  });

  const dismissHow = () => {
    setShowHow(false);
    try {
      localStorage.setItem(HOW_KEY, "1");
    } catch {
      /* nothing to persist to; it will simply show again next time */
    }
  };

  const headName = useCallback(
    (id) => (allUsers || []).find((user) => user.id === id)?.name || null,
    [allUsers],
  );

  const fetchDepartments = useCallback(async () => {
    if (store_id === undefined) return;
    setLoading(true);
    try {
      const res = await departmentService.getDepartmentsByStore(store_id);
      setDepartments(res?.data?.data || []);
      setError("");
      setApiMissing(false);
    } catch (err) {
      if (isEndpointMissing(err)) {
        setApiMissing(true);
        setDepartments([]);
        setError("");
      } else {
        setError(readApiError(err, "Could not load departments."));
      }
    } finally {
      setLoading(false);
    }
  }, [store_id]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // A department is "in hiring use" once anything has been built on it. This
  // matters because the list is now the company's whole department list — the
  // shared table — so most rows have nothing to do with hiring and would
  // otherwise bury the handful that do.
  const isInUse = (dept) =>
    (dept.level_count ?? 0) > 0 ||
    (dept.member_count ?? 0) > 0 ||
    (dept.template_count ?? 0) > 0 ||
    (dept.open_job_count ?? 0) > 0;

  const inUseCount = useMemo(
    () => departments.filter(isInUse).length,
    [departments],
  );

  // Land on the ones being used, unless none are — an empty screen on arrival
  // would be worse than a long list.
  useEffect(() => {
    if (!loading && inUseCount === 0) setScope("all");
  }, [loading, inUseCount]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    let rows = departments;
    if (scope === "active") rows = rows.filter(isInUse);
    if (term)
      rows = rows.filter((dept) => dept.name?.toLowerCase().includes(term));

    // Most-built-out first, then alphabetical. Sorting by name alone put a
    // department with a full structure below a dozen empty ones.
    return [...rows].sort((a, b) => {
      const weight = (d) =>
        (d.open_job_count ?? 0) * 100 +
        (d.template_count ?? 0) * 10 +
        (d.level_count ?? 0);
      const diff = weight(b) - weight(a);
      if (diff !== 0) return diff;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [departments, search, scope]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (dept, event) => {
    event.stopPropagation();
    setEditing(dept);
    setDrawerOpen(true);
  };

  const handleSaved = (saved, { created }) => {
    fetchDepartments();
    // `from` so the new department's back arrow returns to this list rather than
    // to the all-departments chart, which is not where the user has been.
    if (created && saved?.id)
      navigate(routes.detail(saved.id), { state: { from: routes.list } });
  };

  if (store_id === undefined) {
    return <Hint>Sign in to manage hiring departments.</Hint>;
  }

  const ScopeChip = ({ value, label }) => {
    const active = scope === value;
    return (
      <Chip
        label={label}
        onClick={() => setScope(value)}
        sx={{
          fontFamily: T.font,
          fontWeight: 700,
          fontSize: 12.5,
          cursor: "pointer",
          bgcolor: active ? T.accent : T.surface,
          color: active ? "#fff" : T.body,
          border: `1px solid ${active ? T.accent : T.line}`,
          "&:hover": { bgcolor: active ? T.accentDark : T.accentWash },
        }}
      />
    );
  };

  return (
    <DeptTheme>
      <Box sx={{ fontFamily: T.font }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: T.font,
              fontWeight: 800,
              fontSize: { xs: 24, sm: 28 },
              letterSpacing: "-0.02em",
              color: T.ink,
              lineHeight: 1.15,
            }}
          >
            Hiring departments
          </Typography>
          <Hint sx={{ mt: 0.5, maxWidth: 580 }}>
            Your organization chart. Open one to build its teams and roles, then
            hire from them. These are separate from the departments a ticket is
            raised against, unless a department is marked as both.
          </Hint>
        </Box>

        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
          {departments.length > 6 && (
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search departments"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: T.faint }} />
                  </InputAdornment>
                ),
                sx: {
                  fontFamily: T.font,
                  bgcolor: T.surface,
                  borderRadius: T.radius.md,
                },
              }}
            />
          )}
          <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
            New department
          </PrimaryButton>
        </Box>
      </Box>

      {showHow && <HowItWorks onDismiss={dismissHow} />}

      {apiMissing && (
        <Alert
          severity="info"
          sx={{ fontFamily: T.font, mb: 2.5, borderRadius: T.radius.md }}
        >
          This API does not have the hiring department endpoints yet. The screen
          is wired and fills in as soon as they are deployed.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ fontFamily: T.font, mb: 2.5, borderRadius: T.radius.md }}
          action={
            <QuietButton size="small" onClick={fetchDepartments}>
              Try again
            </QuietButton>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && departments.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <ScopeChip value="active" label={`In hiring use · ${inUseCount}`} />
          <ScopeChip value="all" label={`All departments · ${departments.length}`} />
          {scope === "all" && inUseCount < departments.length && (
            <Hint sx={{ fontSize: 12.5 }}>
              Most of these have no hiring setup yet — open one to start.
            </Hint>
          )}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
          <CircularProgress size={26} sx={{ color: T.accent }} />
        </Box>
      ) : departments.length === 0 ? (
        <Box
          sx={{
            bgcolor: T.surface,
            border: `1px solid ${T.line}`,
            borderRadius: T.radius.lg,
          }}
        >
          <EmptyState
            icon={<AccountTreeOutlinedIcon />}
            title="No departments in this store yet"
            body="A department is the top of one hiring structure. You'll build its teams and roles next, then hire from them."
            action={
              <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
                Create a department
              </PrimaryButton>
            }
          />
        </Box>
      ) : visible.length === 0 ? (
        <Box
          sx={{
            bgcolor: T.surface,
            border: `1px solid ${T.line}`,
            borderRadius: T.radius.lg,
          }}
        >
          <EmptyState
            icon={<SearchIcon />}
            title={
              search.trim()
                ? `Nothing matches “${search.trim()}”`
                : "Nothing set up for hiring yet"
            }
            body={
              search.trim()
                ? "Search looks at department names."
                : "Pick any department and start adding its teams and roles."
            }
            action={
              search.trim() ? (
                <QuietButton onClick={() => setSearch("")}>
                  Clear search
                </QuietButton>
              ) : (
                <PrimaryButton onClick={() => setScope("all")}>
                  Show all departments
                </PrimaryButton>
              )
            }
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.75,
          }}
        >
          {visible.map((dept, idx) => {
            const lead = dept.dept_head_name || headName(dept.head);
            const levels = dept.level_count ?? 0;
            const templates = dept.template_count ?? 0;
            const openJobs = dept.open_job_count ?? 0;
            const people = dept.member_count ?? 0;
            const inUse = isInUse(dept);

            return (
              <Box
                key={dept.id}
                onClick={() =>
                  navigate(routes.detail(dept.id), {
                    state: { from: routes.list },
                  })
                }
                role="button"
                tabIndex={0}
                aria-label={`Open ${dept.name}`}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(routes.detail(dept.id), {
                      state: { from: routes.list },
                    });
                  }
                }}
                sx={{
                  cursor: "pointer",
                  bgcolor: T.surface,
                  border: `1px solid ${inUse ? T.accentLine : T.line}`,
                  borderRadius: T.radius.lg,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  transition: "border-color .18s, box-shadow .18s, transform .18s",
                  // Only the first screenful animates; a stagger across fifty
                  // cards reads as the page struggling to load.
                  animation:
                    idx < 8 ? `hbDeptRise .28s ease ${idx * 0.03}s both` : "none",
                  "&:hover": {
                    borderColor: T.accent,
                    boxShadow: "0 6px 18px rgba(15,23,42,.07)",
                    transform: "translateY(-2px)",
                  },
                  "&:hover .go": { opacity: 1 },
                  "&:focus-visible": {
                    outline: `2px solid ${T.accent}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: T.radius.md,
                      bgcolor: inUse ? T.accentWash : T.canvas,
                      color: inUse ? T.accentDeep : T.muted,
                      display: "grid",
                      placeItems: "center",
                      fontFamily: T.font,
                      fontWeight: 800,
                      fontSize: 13.5,
                      flex: "none",
                    }}
                  >
                    {initialsOf(dept.name)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: T.font,
                        fontWeight: 700,
                        fontSize: 15,
                        color: T.ink,
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={dept.name}
                    >
                      {dept.name || "Unnamed department"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: T.font,
                        fontSize: 12,
                        color: T.faint,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {lead ? `Head · ${lead}` : "No head assigned"}
                      {dept.type === "dept_org" && (
                        /* Marked as both, so it is also in the ticket dropdown.
                           Worth saying on the card: renaming or deleting it here
                           changes what the ticket screens show too. */
                        <Box
                          component="span"
                          sx={{ color: T.muted, fontWeight: 600 }}
                        >
                          {" · "}
                          {scopeLabel(dept.type)}
                        </Box>
                      )}
                    </Typography>
                  </Box>

                  <Tooltip title="Edit name and head">
                    <IconButton
                      size="small"
                      aria-label={`Edit ${dept.name}`}
                      onClick={(event) => openEdit(dept, event)}
                      sx={{ color: T.faint, "&:hover": { color: T.accent } }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pt: 1.25,
                    borderTop: `1px solid ${T.lineSoft}`,
                    minHeight: 34,
                  }}
                >
                  {inUse ? (
                    <>
                      {/* One line of numbers with their names, rather than a row
                          of unlabelled counters. */}
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontSize: 12.5,
                          color: T.body,
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {levels} team{levels === 1 ? "" : "s"} & roles
                        {people ? ` · ${people} people` : ""}
                        {templates
                          ? ` · ${templates} template${templates === 1 ? "" : "s"}`
                          : ""}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {openJobs > 0 && (
                        <Chip
                          label={`${openJobs} open`}
                          size="small"
                          sx={{
                            fontFamily: T.font,
                            fontWeight: 700,
                            fontSize: 11,
                            height: 21,
                            bgcolor: T.okWash,
                            color: T.ok,
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontFamily: T.font,
                          fontSize: 12.5,
                          color: T.faint,
                        }}
                      >
                        Not set up for hiring yet
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                    </>
                  )}

                  <ArrowForwardIcon
                    className="go"
                    sx={{
                      fontSize: 16,
                      color: T.accent,
                      opacity: 0,
                      transition: "opacity .18s",
                      flex: "none",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <DepartmentFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        department={editing}
        store_id={store_id}
        user_id={user_id}
        onSaved={handleSaved}
      />
      </Box>
    </DeptTheme>
  );
};

export default DepartmentsList;
